import { useQueryClient } from "@tanstack/react-query";
import { Form } from "../../../../../../shared/frontend/ui";
import React from "react";
import { useAuth } from "../../app/auth/useAuth";
import { useI18n } from "../../app/i18n/useI18n";
import { globalKey } from "../../app/query/queryKeys";
import { useGlobalStudioContext } from "../../app/studios/useGlobalStudioContext";
import { formatMoney } from "../../app/units/format";
import { useEffectiveUnits } from "../../app/units/useEffectiveUnits";
import { invalidatePersonFinanceData } from "../../pages/persons/personDetail/personDetailQueryKeys";
import { useFeedback } from "../../../../../../shared/frontend/feedback/useFeedback";
import { useFormDirty } from "../../../../../../shared/frontend/forms/useFormDirty";
import { useDirtySnapshot } from "../../../../../../shared/frontend/forms/useDirtySnapshot";

const CENTS_PER_UNIT = 100;
/** Beträge unterhalb eines halben Cent gelten als gleich. */
const MONEY_ROUNDING_TOLERANCE = 0.005;

export type CancellableContribution = {
  id: string; personId: string; personName: string; description: string; dueDate: string;
  amount: number; cancelledAmount: number; canCancel: boolean; remainingAmount: number;
};
export type ContributionCancellationActionProps = {
  personId?: string; openItemId?: string; selectedOpenItemIds?: string[]; onChanged: () => void; compact?: boolean;
};
export type ContributionCancellationValues = { amount: number; reasonId: string; reason?: string };
type CancellationResponse = { walletTransactionId?: string; walletAmount?: number };

export function useContributionCancellation({ personId, openItemId, selectedOpenItemIds, onChanged }: ContributionCancellationActionProps) {
  const auth = useAuth();
  const { text } = useI18n();
  const feedback = useFeedback();
  const units = useEffectiveUnits();
  const { appendStudioIdsQuery } = useGlobalStudioContext();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<ContributionCancellationValues>();
  const draft = useFormDirty(form);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [loadFailed, setLoadFailed] = React.useState(false);
  const [items, setItems] = React.useState<CancellableContribution[]>([]);
  const [reasons, setReasons] = React.useState<Array<{ id: string; name: string }>>([]);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const selectionDraft = useDirtySnapshot([...selectedIds].sort());
  const requestVersion = React.useRef(0);
  const selectedItems = items.filter((item) => selectedIds.includes(item.id));
  const maxAmount = Math.round(selectedItems.reduce((sum, item) => sum + item.remainingAmount, 0) * CENTS_PER_UNIT) / CENTS_PER_UNIT;
  const amount = Form.useWatch("amount", form) ?? 0;
  const reasonId = Form.useWatch("reasonId", form);
  const allocations = new Map<string, number>();
  let remaining = Math.round(Number(amount) * CENTS_PER_UNIT);
  for (const item of selectedItems) {
    const allocated = Math.max(0, Math.min(remaining, Math.round(item.remainingAmount * CENTS_PER_UNIT)));
    allocations.set(item.id, allocated / CENTS_PER_UNIT);
    remaining -= allocated;
  }
  const close = () => { requestVersion.current++; setOpen(false); };
  const select = (keys: React.Key[]) => {
    const ids = keys.map(String);
    setSelectedIds(ids);
    form.setFieldValue("amount", Math.round(items.filter((item) => ids.includes(item.id))
      .reduce((sum, item) => sum + item.remainingAmount, 0) * CENTS_PER_UNIT) / CENTS_PER_UNIT);
  };
  const show = async () => {
    const version = ++requestVersion.current;
    form.resetFields();
    draft.resetDirty();
    selectionDraft.markSaved([]);
    setItems([]); setReasons([]); setSelectedIds([]); setLoadFailed(false); setOpen(true); setLoading(true);
    try {
      const params = new URLSearchParams({ includeSettled: "true" });
      if (personId) params.set("personId", personId);
      const [rows, availableReasons] = await Promise.all([
        auth.api.get<CancellableContribution[]>(appendStudioIdsQuery(`/api/v1/open-items?${params}`)),
        auth.api.get<Array<{ id: string; name: string }>>("/api/v1/contribution-cancellation-reasons/available")
      ]);
      if (version !== requestVersion.current) return;
      const eligible = rows.filter((row) => row.canCancel && (!openItemId || row.id === openItemId))
        .map((row) => ({ ...row, remainingAmount: Math.round((row.amount - row.cancelledAmount) * CENTS_PER_UNIT) / CENTS_PER_UNIT }));
      const initialIds = new Set(openItemId ? [openItemId] : selectedOpenItemIds ?? []);
      const initial = eligible.filter((row) => initialIds.has(row.id));
      setItems(eligible); setReasons(availableReasons); setSelectedIds(initial.map((row) => row.id));
      draft.initialize({ amount: Math.round(initial.reduce((sum, item) => sum + item.remainingAmount, 0) * CENTS_PER_UNIT) / CENTS_PER_UNIT });
      selectionDraft.markSaved(initial.map(item => item.id).sort());
    } catch {
      // The ApiClient reports the cause; keep a failed selection unsaveable.
      if (version === requestVersion.current) setLoadFailed(true);
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  };
  const save = async (values: ContributionCancellationValues) => {
    setSaving(true);
    try {
      const result = await auth.api.post<CancellationResponse[]>("/api/v1/open-items/cancel-bulk", {
        openItemIds: selectedItems.map((item) => item.id), partialAmount: values.amount,
        contributionCancellationReasonId: values.reasonId, reasonText: values.reason?.trim()
      });
      const walletAmount = result.reduce((sum, item) => sum + (item.walletAmount ?? 0), 0);
      feedback.success(result.some((item) => item.walletTransactionId)
        ? text("openItems.cancel.successWithWallet").replace("{0}", formatMoney(walletAmount, units))
        : text("openItems.cancel.successWithoutWallet"));
      for (const pid of new Set(selectedItems.map((item) => item.personId))) invalidatePersonFinanceData(queryClient, pid);
      void queryClient.invalidateQueries({ queryKey: globalKey("reports") });
      close(); onChanged();
    } catch {
      // The transaction remains unchanged on failure; retain selection and form values.
    } finally { setSaving(false); }
  };
  return { auth, form, open, loading, saving, loadFailed, items, reasons, selectedIds, maxAmount, allocations,
    dirty: draft.dirty || selectionDraft.dirty,
    isPartial: Number(amount) > 0 && Number(amount) < maxAmount - MONEY_ROUNDING_TOLERANCE,
    canSubmit: selectedItems.length > 0 && Number(amount) > 0 && reasons.some((reason) => reason.id === reasonId) && !loadFailed,
    show, close, select, save };
}
